import { Checkbox, TextField } from '@mui/material';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Send, Close, QuestionAnswer, CheckCircle } from '@mui/icons-material';

import Modal from '../components/modals/Modal';
import { useInquiry } from '../context/InquiryContext';

type Props = {
  isDialogOpen: boolean;
  setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  products: { productId: number; quantity: number }[];
  singleProduct?: boolean;
  callback?: () => void;
};

type FormData = {
  name: string;
  nip: string;
  email: string;
  remarks: string;
  rulesAccepted: boolean;
  quantity: number;
};

function InquiryForm({
  isDialogOpen,
  setIsDialogOpen,
  products,
  singleProduct,
  callback,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      nip: '',
      email: '',
      remarks: '',
      quantity: 1,
      rulesAccepted: false,
    },
  });

  const { createInquiry } = useInquiry();

  const handleDialogClose = () => {
    if (isSubmitting) return;
    setIsDialogOpen(false);

    // Reset statów po zamknięciu modalu
    setTimeout(() => {
      setIsSuccess(false);
      reset();
    }, 300);
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      const inquiryConfig = {
        name: data.name,
        email: data.email,
        products: [...products],
        nip: data.nip,
        remarks: data.remarks,
      };

      if (singleProduct) {
        inquiryConfig.products[0].quantity = data.quantity;
      }

      await createInquiry(
        data.name,
        data.email,
        inquiryConfig.products,
        data.nip,
        data.remarks,
      );

      setIsSuccess(true);

      // Zamknij formularz po 2 sekundach od powodzenia
      setTimeout(() => {
        handleDialogClose();
        if (callback) {
          callback();
        }
      }, 2000);
    } catch (error: any) {
      console.error('Failed to process inquiry', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <Modal show={isDialogOpen} onClose={handleDialogClose} size="md">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="sm:min-w-[400px]"
      >
        {/* Nagłówek */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center gap-3 mb-6"
        >
          <div className="bg-primary bg-opacity-10 rounded-full p-2">
            {isSuccess ? (
              <CheckCircle className="text-green-600" />
            ) : (
              <QuestionAnswer className="text-primary" />
            )}
          </div>
          <h2 className="text-xl font-medium text-gray-900">
            {isSuccess
              ? 'Zapytanie zostało wysłane!'
              : `Zapytanie o produkt${singleProduct ? '' : 'y'}`}
          </h2>
        </motion.div>

        {isSuccess ? (
          <motion.div
            variants={itemVariants}
            className="bg-green-50 rounded-lg p-6 mb-6 text-center"
          >
            <p className="text-green-700 font-medium mb-2">
              Dziękujemy za złożenie zapytania!
            </p>
            <p className="text-gray-600">
              Odpowiemy na Twoje pytanie najszybciej jak to możliwe.
            </p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <motion.div variants={itemVariants} className="mb-4">
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Imię i Nazwisko / Firma jest wymagane' }}
                render={({ field }) => (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Imię i Nazwisko / Firma *
                    </label>
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="Wpisz imię i nazwisko lub nazwę firmy"
                      size="small"
                      className="bg-gray-50 rounded"
                    />
                  </div>
                )}
              />
            </motion.div>

            <motion.div variants={itemVariants} className="mb-4">
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email jest wymagany',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Niepoprawny format adresu email',
                  },
                }}
                render={({ field }) => (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      placeholder="Twój adres email"
                      size="small"
                      className="bg-gray-50 rounded"
                    />
                  </div>
                )}
              />
            </motion.div>

            <motion.div variants={itemVariants} className="mb-4">
              <Controller
                name="nip"
                control={control}
                render={({ field }) => (
                  <div className="mb-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      NIP
                    </label>
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="NIP firmy (opcjonalnie)"
                      size="small"
                      className="bg-gray-50 rounded"
                    />
                  </div>
                )}
              />
              <p className="text-xs text-gray-500 mb-4">
                Podaj NIP, jeśli chcesz otrzymać ofertę dla firmy.
              </p>
            </motion.div>

            {singleProduct && (
              <motion.div variants={itemVariants} className="mb-4">
                <Controller
                  name="quantity"
                  control={control}
                  rules={{ required: 'Ilość jest wymagana', min: 1 }}
                  render={({ field }) => (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ilość *
                      </label>
                      <TextField
                        {...field}
                        type="number"
                        fullWidth
                        error={!!errors.quantity}
                        helperText={errors.quantity?.message}
                        InputProps={{ inputProps: { min: 1 } }}
                        size="small"
                        className="bg-gray-50 rounded"
                      />
                    </div>
                  )}
                />
              </motion.div>
            )}

            <motion.div variants={itemVariants} className="mb-6">
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Uwagi
                    </label>
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Dodatkowe informacje, pytania lub uwagi"
                      size="small"
                      className="bg-gray-50 rounded"
                    />
                  </div>
                )}
              />
            </motion.div>

            <motion.div variants={itemVariants} className="mb-6">
              <Controller
                name="rulesAccepted"
                control={control}
                rules={{ required: 'Musisz zaakceptować zasady' }}
                render={({ field }) => (
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <Checkbox
                        {...field}
                        checked={field.value}
                        color="primary"
                        className="p-0 mr-2"
                        size="small"
                      />
                    </div>
                    <div className="ml-1 text-sm">
                      <label className="font-medium text-gray-700">
                        Wyrażam zgodę na przetwarzanie danych osobowych w celu
                        realizacji zapytania. *
                      </label>
                      {errors.rulesAccepted && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.rulesAccepted.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              />
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex sm:flex-row flex-col gap-3"
            >
              <motion.button
                type="button"
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-md font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                onClick={handleDialogClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSubmitting}
              >
                <Close fontSize="small" />
                <span>Anuluj</span>
              </motion.button>

              <motion.button
                type="submit"
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-md font-medium flex items-center justify-center gap-2 hover:bg-primary-dark transition-colors disabled:bg-gray-400"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSubmitting}
              >
                <Send fontSize="small" />
                <span>
                  {isSubmitting ? 'Wysyłanie...' : 'Wyślij zapytanie'}
                </span>
              </motion.button>
            </motion.div>
          </form>
        )}
      </motion.div>
    </Modal>
  );
}

export default InquiryForm;
